import Phaser from 'phaser';
import { Player } from '@/entities/Player';
import { PetFollower } from '@/entities/PetFollower';
import { NPC, getNpcsForMap } from '@/entities/NPC';
import { Pet } from '@/network/RestApi';
import { isPhaserPointerOverBlockingUI } from '@/ui/pointerBlock';
import { ensureRealtime } from '@/network/realtime';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { useTeleportStore } from '@/state/teleportStore';
import { useDeathStore } from '@/state/deathStore';
import { chat } from '@/systems/ChatSystem';
import type { NearbyChatMsg } from '@/systems/ChatSystem';
import { TEX } from '@/data/assetManifest';
import { AudioManager } from '@/systems/AudioManager';
import { ensureMapBase } from '@/maps/baseLoader';
import { renderMapModule } from '@/maps/runtime';
import { compile } from '@/maps/types';
import { loadMapModule } from '@/maps/registry';
import type { TownSnapshot, PlayerStateMsg } from '@/network/messages';
import { AmbientLightSystem } from '@/systems/AmbientLightSystem';
import { WeatherFXSystem } from '@/systems/WeatherFXSystem';
import { PlayerLightSystem } from '@/systems/PlayerLightSystem';
import { ingestClockSnapshot } from '@/state/clockStore';

const ROOM_ROUTE = 'town.join';
const MOVE_ROUTE = 'town.move';
const STATE_PUSH = 'state';

// MAP_01 Lorencia: 80x80 tiles, 32px each = 2560x2560 px
const TILE = 32;
const MAP_W = 80;
const MAP_H = 80;
const WORLD_W = MAP_W * TILE; // 2560
const WORLD_H = MAP_H * TILE; // 2560


// Gate positions (tile coords → pixel center)
const GATES = {
  north: { tx: 40, ty: 2,  px: 40 * TILE, py: 2 * TILE,  label: 'Cổng Bắc\n[Noria]',   color: '#3498db', target: 'Field',   minLv: 1  },
  east:  { tx: 77, ty: 40, px: 77 * TILE, py: 40 * TILE, label: 'Cổng Đông\n[Devias]',  color: '#27ae60', target: 'Field',   minLv: 30 },
  south: { tx: 40, ty: 77, px: 40 * TILE, py: 77 * TILE, label: 'Cổng Nam\n[Ngoại Ô]',  color: '#e74c3c', target: 'Field',   minLv: 1  },
  west:  { tx: 2,  ty: 40, px: 2 * TILE,  py: 40 * TILE, label: 'Cổng Tây\n[Dungeon]', color: '#8e44ad', target: 'Dungeon', minLv: 60 },
};

// Spawn points (spec section 8)
const SPAWN = { x: 40 * TILE, y: 44 * TILE }; // tile (40,44) — south of fountain

export class TownScene extends Phaser.Scene {
  private players: Map<string, Player> = new Map();
  private npcs: NPC[] = [];
  private localPlayer?: Player;
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasdKeys!: Record<'W' | 'A' | 'S' | 'D', Phaser.Input.Keyboard.Key>;
  private lastSent = 0;
  private localUid = '';
  private unsubState?: () => void;
  private unsubNearby?: () => void;
  private sortables: Phaser.GameObjects.Container[] = [];
  private gateZones: Array<{ key: string; cfg: typeof GATES[keyof typeof GATES]; radius: number }> = [];
  private gateCooldownUntil = 0;
  private petFollower?: PetFollower;
  private blockers: Array<{ x: number; y: number; w: number; h: number }> = [];
  private ambient?: AmbientLightSystem;
  private weatherFx?: WeatherFXSystem;
  private playerLight?: PlayerLightSystem;

  constructor() { super('Town'); }

  async create() {
    useDeathStore.getState().hide();

    this.cameras.main.setBounds(0, 0, WORLD_W, WORLD_H);
    this.physics.world.setBounds(0, 0, WORLD_W, WORLD_H);

    this.sortables = [];
    this.blockers = [];

    // Render Lorencia identically to the in-app Map Editor:
    //   1) AI-generated base PNG as ground
    //   2) Layered props from client/src/maps/Lorencia.map.tsx via renderMapModule
    // The legacy tile-grid + procedural path used to overlay tiny16 art on top
    // of the PNG which is what produced the visual mismatch with the editor.
    await ensureMapBase(this, 'lorencia');
    const node = await loadMapModule('lorencia');
    if (node) {
      const mod = compile(node);
      const rendered = renderMapModule(this, mod);
      this.blockers.push(...rendered.blockers.map((b) => ({ x: b.x, y: b.y, w: b.w, h: b.h })));
    }

    this.spawnLayeredProps();
    this.spawnNpcs();
    this.spawnGates();

    AudioManager.play(this, 'bgm_lorencia');

    this.add.text(20, 20, 'Lorencia (Safe Zone)', {
      fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '18px', color: '#fff',
      stroke: '#000', strokeThickness: 3,
    }).setScrollFactor(0).setDepth(100);

    // Input
    this.cursors = this.input.keyboard!.createCursorKeys();
    this.wasdKeys = this.input.keyboard!.addKeys('W,A,S,D') as typeof this.wasdKeys;
    this.input.keyboard!.removeCapture('W,A,S,D');

    // Realtime connection
    const ch = useCharacterStore.getState().current;
    if (!ch) { this.scene.start('CharacterSelect'); return; }
    useCharacterStore.getState().setPosition({ x: SPAWN.x, y: SPAWN.y, mapId: 1 });
    useTeleportStore.getState().markVisited(1);

    try {
      const cli = await ensureRealtime();
      const resp = await cli.request<{ ok: boolean; uid: string }>(ROOM_ROUTE, { name: ch.name, charId: ch.id });
      this.localUid = resp.uid;
      this.spawnLocal(SPAWN.x, SPAWN.y);
      this.unsubState = cli.on(STATE_PUSH, (data) => this.onState(data as TownSnapshot));
      chat.setRoom('town');
      chat.setLocalUid(this.localUid);
      chat.bindWebSocket();
      this.unsubNearby = chat.onNearbyChat((m: NearbyChatMsg) => {
        this.players.get(m.uid)?.showBubble(m.text);
      });
      useUIStore.getState().showToast('Đã vào Lorencia', 'info');
    } catch (e) {
      useUIStore.getState().showToast(`Kết nối thất bại: ${(e as Error).message}`, 'error');
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubState?.();
      this.unsubNearby?.();
      this.petFollower?.destroy();
      this.petFollower = undefined;
      this.ambient?.destroy(); this.ambient = undefined;
      this.weatherFx?.destroy(); this.weatherFx = undefined;
      this.playerLight?.destroy(); this.playerLight = undefined;
      chat.unbind();
      ensureRealtime().then((cli) => cli.notify('town.leave', {})).catch(() => {});
    });

    this.ambient = new AmbientLightSystem(this);
    this.weatherFx = new WeatherFXSystem(this);
    this.playerLight = new PlayerLightSystem(this);
  }

  // Check if a point collides with any blocker rect (AABB test with player radius).
  private isBlocked(px: number, py: number, radius = 12): boolean {
    for (const b of this.blockers) {
      if (
        px + radius > b.x &&
        px - radius < b.x + b.w &&
        py + radius > b.y &&
        py - radius < b.y + b.h
      ) {
        return true;
      }
    }
    return false;
  }

  private spawnLocal(x: number, y: number) {
    // Guard: scene may have been shut down while awaiting realtime/request.
    // Without these checks, re-entering the town can throw
    // "Cannot read properties of undefined (reading 'startFollow')".
    if (!this.scene || !this.scene.isActive() || !this.sys || !this.sys.cameras) {
      return;
    }
    const ch = useCharacterStore.getState().current!;
    this.localPlayer = new Player({ scene: this, x, y, uid: this.localUid, name: ch.name, klass: ch.class, gender: ch.gender, isLocal: true });
    this.players.set(this.localUid, this.localPlayer);
    if (this.cameras && this.cameras.main) {
      this.cameras.main.startFollow(this.localPlayer, true, 0.1, 0.1);
    }
    this.sortables.push(this.localPlayer);
    this.spawnPet();
  }

  private async spawnPet() {
    if (!this.localPlayer) return;
    const ch = useCharacterStore.getState().current;
    if (!ch) return;
    try {
      const { pets } = await Pet.list(ch.id);
      const active = pets?.find((p) => p.active);
      if (!active || !this.localPlayer) return;
      const { templates } = await Pet.templates();
      const tpl = templates?.find((t) => t.id === active.templateId);
      this.petFollower = new PetFollower({
        scene: this,
        owner: this.localPlayer,
        name: active.nickname || tpl?.name || `Pet`,
        tier: tpl?.tier ?? 1,
        petCode: tpl?.code,
      });
    } catch {
      // No pet or API unavailable — silently skip
    }
  }

  update(time: number, delta: number) {
    // Interpolate remote players even before local spawns
    for (const [uid, pl] of this.players) {
      if (uid !== this.localUid) pl.preUpdate(time, delta);
    }

    this.ambient?.update();
    this.weatherFx?.update(time, delta);
    if (this.localPlayer) this.playerLight?.setTarget(this.localPlayer);
    this.playerLight?.update(time);

    // Y-sort depth for all entities in the group
    for (const s of this.sortables) {
      if (s.active) s.setDepth(s.y);
    }

    if (this.petFollower) this.petFollower.update(time, delta);

    if (!this.localPlayer) return;

    // Skip movement if typing in chat
    const el = document.activeElement;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;

    const speed = 0.18 * delta;
    let dx = 0, dy = 0;
    if (this.cursors.left.isDown || this.wasdKeys.A.isDown) dx -= 1;
    if (this.cursors.right.isDown || this.wasdKeys.D.isDown) dx += 1;
    if (this.cursors.up.isDown || this.wasdKeys.W.isDown) dy -= 1;
    if (this.cursors.down.isDown || this.wasdKeys.S.isDown) dy += 1;

    if (dx || dy) {
      const len = Math.hypot(dx, dy);
      let nx = this.localPlayer.x + (dx / len) * speed;
      let ny = this.localPlayer.y + (dy / len) * speed;
      // Clamp to world bounds
      nx = Phaser.Math.Clamp(nx, 16, WORLD_W - 16);
      ny = Phaser.Math.Clamp(ny, 16, WORLD_H - 16);
      // Collision: slide along blockers (try X then Y independently).
      // Skip check if player is already inside a blocker (let them escape).
      if (this.blockers.length > 0 && !this.isBlocked(this.localPlayer.x, this.localPlayer.y)) {
        if (this.isBlocked(nx, this.localPlayer.y)) nx = this.localPlayer.x;
        if (this.isBlocked(this.localPlayer.x, ny)) ny = this.localPlayer.y;
        if (this.isBlocked(nx, ny)) { nx = this.localPlayer.x; ny = this.localPlayer.y; }
      }
      this.localPlayer.x = nx;
      this.localPlayer.y = ny;

      const dir = dx > 0 ? 1 : dx < 0 ? 3 : dy > 0 ? 2 : 0;
      useCharacterStore.getState().setPosition({ x: nx, y: ny });

      // Throttle network move 10Hz
      if (time - this.lastSent > 100) {
        this.lastSent = time;
        ensureRealtime().then((cli) => {
          cli.notify(MOVE_ROUTE, { x: this.localPlayer!.x, y: this.localPlayer!.y, d: dir });
        }).catch(() => {});
      }
    }

    // Auto-enter gate if player walked into the aura
    if (time >= this.gateCooldownUntil) {
      for (const z of this.gateZones) {
        const dxg = this.localPlayer.x - z.cfg.px;
        const dyg = this.localPlayer.y - z.cfg.py;
        if (dxg * dxg + dyg * dyg <= z.radius * z.radius) {
          this.gateCooldownUntil = time + 1500;
          this.tryEnterGate(z.cfg);
          break;
        }
      }
    }
  }

  // --- State sync (unchanged protocol) ---
  private onState(snap: TownSnapshot) {
    if (!snap?.players) return;
    if (snap.clock) ingestClockSnapshot(snap.clock);
    const seen = new Set<string>();
    for (const ps of snap.players) {
      seen.add(ps.uid);
      if (ps.uid === this.localUid) {
        const st = useCharacterStore.getState();
        const patch: { hp?: number; mp?: number; maxHp?: number; maxMp?: number } = {};
        if (typeof ps.hp === 'number' && ps.hp > 0) {
          patch.hp = ps.hp;
          if (typeof ps.maxHp === 'number' && ps.maxHp > 0) patch.maxHp = ps.maxHp;
          else if (ps.hp > st.maxHp) patch.maxHp = ps.hp;
        }
        if (typeof ps.mp === 'number' && ps.mp >= 0) {
          patch.mp = ps.mp;
          if (typeof ps.maxMp === 'number' && ps.maxMp > 0) patch.maxMp = ps.maxMp;
          else if (ps.mp > st.maxMp) patch.maxMp = ps.mp;
        }
        if (Object.keys(patch).length) st.setVitals(patch);
        continue;
      }
      let pl = this.players.get(ps.uid);
      if (!pl) {
        pl = new Player({ scene: this, x: ps.x, y: ps.y, uid: ps.uid, name: ps.name, klass: this.guessClass(ps) });
        this.players.set(ps.uid, pl);
        this.sortables.push(pl);
      }
      pl.setRemoteTarget(ps.x, ps.y);
      pl.setHp(ps.hp, Math.max(ps.hp, pl.maxHp));
    }
    for (const uid of [...this.players.keys()]) {
      if (uid !== this.localUid && !seen.has(uid)) {
        const removed = this.players.get(uid)!;
        this.sortables = this.sortables.filter((s) => s !== removed);
        removed.destroy();
        this.players.delete(uid);
      }
    }
  }

  private guessClass(_p: PlayerStateMsg): 'warrior' | 'wizard' | 'archer' {
    return 'warrior';
  }

  // --- Layered generated props (transparent PNGs from generate2dsprite) ---
  // These are higher-fidelity replacements for some tiny16 placeholder props.
  // Placed with Y-sort depth so actors walk in front of / behind them correctly.
  private spawnLayeredProps() {
    // Animated fountain at plaza center
    if (this.textures.exists(TEX.propFountainIdle)) {
      if (!this.anims.exists('fountain_idle')) {
        this.anims.create({
          key: 'fountain_idle',
          frames: this.anims.generateFrameNumbers(TEX.propFountainIdle, { start: 0, end: 3 }),
          frameRate: 4,
          repeat: -1,
        });
      }
      const fountain = this.add.sprite(40 * TILE, 40 * TILE, TEX.propFountainIdle);
      fountain.setDisplaySize(128, 128);
      fountain.setDepth(40 * TILE + 64);
      fountain.play('fountain_idle');
    } else if (this.textures.exists(TEX.propFountainGen)) {
      // Fallback to static image
      const img = this.add.image(40 * TILE, 40 * TILE, TEX.propFountainGen);
      img.setDisplaySize(128, 128);
      img.setDepth(40 * TILE + 64);
    }
  }

  // --- 4 Gates (spec section 2) ---
  private spawnGates() {
    const gateTexMap: Record<string, string> = {
      north: TEX.gateNorth,
      east: TEX.gateEast,
      south: TEX.gateSouth,
      west: TEX.gateWest,
    };

    this.gateZones = [];

    for (const [dir, g] of Object.entries(GATES)) {
      const tex = gateTexMap[dir];
      const colorNum = Phaser.Display.Color.HexStringToColor(g.color).color;

      // Aura: outer soft ring + inner bright ring + rotating runic ring
      const auraOuter = this.add.circle(g.px, g.py, 70, colorNum, 0.18).setDepth(4);
      const auraInner = this.add.circle(g.px, g.py, 44, colorNum, 0.32).setDepth(4);
      const auraRing = this.add.graphics().setDepth(4);
      auraRing.lineStyle(3, colorNum, 0.85);
      auraRing.strokeCircle(0, 0, 56);
      auraRing.lineStyle(2, 0xffffff, 0.45);
      auraRing.strokeCircle(0, 0, 38);
      auraRing.setPosition(g.px, g.py);

      this.tweens.add({
        targets: [auraOuter, auraInner],
        scale: { from: 0.85, to: 1.15 },
        alpha: { from: 0.5, to: 0.9 },
        duration: 1100,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: auraRing,
        rotation: Math.PI * 2,
        duration: 6000,
        repeat: -1,
        ease: 'Linear',
      });

      // Sparkle particles rising from the gate base
      this.time.addEvent({
        delay: 220,
        loop: true,
        callback: () => {
          const sp = this.add.circle(
            g.px + Phaser.Math.Between(-28, 28),
            g.py + 20,
            Phaser.Math.Between(2, 4),
            colorNum, 0.9,
          ).setDepth(g.py + 1);
          this.tweens.add({
            targets: sp,
            y: sp.y - Phaser.Math.Between(40, 70),
            alpha: 0,
            duration: 900,
            onComplete: () => sp.destroy(),
          });
        },
      });

      let gate: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;
      if (this.textures.exists(TEX.propPortalGateIdle)) {
        if (!this.anims.exists('portal_gate_idle')) {
          this.anims.create({
            key: 'portal_gate_idle',
            frames: this.anims.generateFrameNumbers(TEX.propPortalGateIdle, { start: 0, end: 3 }),
            frameRate: 5,
            repeat: -1,
          });
        }
        const sprite = this.add.sprite(g.px, g.py, TEX.propPortalGateIdle).setDepth(5);
        sprite.setDisplaySize(96, 96);
        sprite.play({ key: 'portal_gate_idle', startFrame: Math.floor(Math.random() * 4) });
        gate = sprite;
      } else {
        gate = this.add.image(g.px, g.py, tex).setDepth(5);
      }
      gate.setInteractive({ useHandCursor: true });

      this.add.text(g.px, g.py + 52, g.label, {
        fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '13px', color: g.color,
        stroke: '#000', strokeThickness: 3, align: 'center',
      }).setOrigin(0.5, 0).setDepth(6);

      // Pulsing glow
      this.tweens.add({
        targets: gate, alpha: { from: 0.75, to: 1 },
        duration: 900 + Math.random() * 400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });

      const tooltipName = `gate_tooltip_${dir}`;
      gate.on('pointerover', () => {
        this.add.text(g.px, g.py - 60, `Bước vào để đi ${g.label.replace('\n', ' ')}`, {
          fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '11px', color: '#fff',
          backgroundColor: '#1a1a2e', padding: { x: 6, y: 4 }, align: 'center',
        }).setOrigin(0.5).setDepth(20).setName(tooltipName);
      });
      gate.on('pointerout', () => this.children.getByName(tooltipName)?.destroy());
      gate.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        if (!isPhaserPointerOverBlockingUI(pointer)) this.tryEnterGate(g);
      });

      // Register trigger zone (radius in px) for auto-enter on overlap
      this.gateZones.push({ key: dir, cfg: g, radius: 56 });
    }
  }

  private tryEnterGate(g: typeof GATES[keyof typeof GATES]) {
    const ch = useCharacterStore.getState().current;
    if (ch && ch.level < g.minLv) {
      useUIStore.getState().showToast(`Cần đạt Lv.${g.minLv} để vào`, 'warn');
      // Brief cooldown to avoid spamming the toast while standing on the gate
      this.gateCooldownUntil = this.time.now + 2000;
      return;
    }
    // Map gate direction → destination map id so FieldScene picks the right theme.
    const dirToMapId: Record<string, number> = {
      north: 2,   // Noria
      east: 3,    // Devias
      south: 100, // Lorencia outskirt (Spider/Bandit/Knight Apprentice/Rat - MQ02/MQ04, SQ003/SQ005)
      west: 4,    // Dungeon
    };
    const dir = (Object.entries(GATES).find(([, v]) => v === g)?.[0]) ?? '';
    const targetMapId = dirToMapId[dir];
    if (targetMapId && targetMapId !== 1) {
      // Spawn the player a safe distance away from every map edge gate so they
      // do not immediately re-trigger a return gate (auto-enter radius 56).
      // For lorencia_outskirt (south gate of Lorencia), spawn at the top of
      // the outskirt field so the player walks down to anchors.
      const spawn = targetMapId === 100 ? { x: 960, y: 200 } : { x: 960, y: 900 };
      useCharacterStore.getState().setPosition({ x: spawn.x, y: spawn.y, mapId: targetMapId });
    }
    this.scene.start(g.target);
  }

  // --- NPCs ---
  private spawnNpcs() {
    const npcDefs = getNpcsForMap('lorencia');
    for (const def of npcDefs) {
      const npc = new NPC({
        scene: this,
        id: def.id,
        name: def.name,
        role: def.role,
        x: def.x,
        y: def.y,
        color: parseInt(def.color),
        dialog: def.dialog,
        sprite: def.sprite,
        spriteFrame: def.spriteFrame,
      });
      this.npcs.push(npc);
      this.sortables.push(npc);
    }
  }
}
